import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { PosAgentAdminService } from './pos-agent-admin.service';

export interface PosTablet {
  socketId: string;
  storeId: string;
  storeName?: string;
  chainId: string;
  terminalId: string;
  terminalName: string;
  managerId: string;
  managerName?: string;
  connectedAt: string;
  buildCommit?: string;
  buildDate?: string;
  flavor?: string;
  staleVsProd?: boolean;
  prodBuildCommit?: string;
}

export interface RemoteControlSession {
  controlId: string;
  posSocketId: string;
  stream: MediaStream | null;
}

export interface PosAgent {
  terminalId: string;
  storeId: string;
  chainId: string;
  screenWidth: number;
  screenHeight: number;
  connectedAt: string;
}

export interface DesktopSession {
  controlId: string;
  terminalId: string;
  screenWidth: number;
  screenHeight: number;
}

@Injectable({ providedIn: 'root' })
export class RemoteControlAdminService {
  onlineTablets$ = new BehaviorSubject<PosTablet[]>([]);
  activeSession$ = new BehaviorSubject<RemoteControlSession | null>(null);

  // Desktop View (pos-agent:*)
  onlineAgents$ = new BehaviorSubject<PosAgent[]>([]);
  activeDesktopSession$ = new BehaviorSubject<DesktopSession | null>(null);
  currentFrameSrc$ = new BehaviorSubject<string | null>(null);

  private socket: Socket | null = null;
  private peerConnection: RTCPeerConnection | null = null;
  private currentControlId: string | null = null;
  private currentDesktopControlId: string | null = null;
  private lastMoveEmitMs = 0;
  private lastDesktopMoveEmitMs = 0;

  private readonly configuration: RTCConfiguration = {
    iceServers: [
      {
        urls: 'turn:a.relay.metered.ca:80',
        username: '0ed1b4fdac3609a8397799ff',
        credential: 'RP5dQKcnuNusMqcc',
      },
      {
        urls: 'turn:a.relay.metered.ca:80?transport=tcp',
        username: '0ed1b4fdac3609a8397799ff',
        credential: 'RP5dQKcnuNusMqcc',
      },
      {
        urls: 'turn:a.relay.metered.ca:443',
        username: '0ed1b4fdac3609a8397799ff',
        credential: 'RP5dQKcnusMqcc',
      },
      {
        urls: 'turn:a.relay.metered.ca:443?transport=tcp',
        username: '0ed1b4fdac3609a8397799ff',
        credential: 'RP5dQKcnuNusMqcc',
      },
      {
        urls: ['stun:stun1.l.google.com:19302', 'stun:stun2.l.google.com:19302', 'stun:a.relay.metered.ca:80'],
      },
    ],
    iceCandidatePoolSize: 10,
  };

  constructor(
    private http: HttpClient,
    private posAgentAdminService: PosAgentAdminService,
  ) {}

  getSocket(): Socket | null {
    return this.socket;
  }

  connect(adminId: string, role: string): void {
    if (this.socket?.connected) return;

    const socketUrl = environment.baseUrl.replace('/api/v1/', '');
    const token = localStorage.getItem('token');

    // Reset listener guard so PosAgentAdminService re-registers on the new socket
    this.posAgentAdminService.resetListenerGuard();

    this.socket = io(socketUrl, {
      auth: { token },
      transports: ['websocket'],
    });

    this.socket.on('connect', () => {
      this.socket?.emit('pos:admin-join', { adminId, role });
    });

    this.socket.on('pos:tablet-online', (tablet: PosTablet) => {
      const current = this.onlineTablets$.getValue();
      if (!current.find(t => t.socketId === tablet.socketId)) {
        this.onlineTablets$.next([...current, tablet]);
      }
    });

    this.socket.on('pos:tablet-offline', (data: { socketId: string }) => {
      const current = this.onlineTablets$.getValue();
      this.onlineTablets$.next(current.filter(t => t.socketId !== data.socketId));
    });

    this.socket.on('pos:control-ready', (data: { controlId: string; posSocketId: string }) => {
      this.currentControlId = data.controlId;
      this.activeSession$.next({ controlId: data.controlId, posSocketId: data.posSocketId, stream: null });
    });

    this.socket.on('pos:offer', (data: { controlId: string; sdp: RTCSessionDescriptionInit }) => {
      if (data.controlId === this.currentControlId) {
        this.handleOffer(data.sdp);
      }
    });

    this.socket.on('pos:ice-candidate', (data: { controlId: string; candidate: RTCIceCandidateInit }) => {
      if (data.controlId === this.currentControlId) {
        this.handleIceCandidate(data.candidate);
      }
    });

    this.socket.on('pos:control-ended', (data: { controlId: string }) => {
      if (data.controlId === this.currentControlId) {
        this.cleanup();
      }
    });

    // ── Desktop View (pos-agent:*) ──────────────────────────────────────────
    this.socket.on('pos-agent:online', (agent: PosAgent) => {
      const current = this.onlineAgents$.getValue();
      const updated = current.filter(a => a.terminalId !== agent.terminalId);
      this.onlineAgents$.next([...updated, agent]);
    });

    this.socket.on('pos-agent:offline', (data: { terminalId: string }) => {
      const current = this.onlineAgents$.getValue();
      this.onlineAgents$.next(current.filter(a => a.terminalId !== data.terminalId));
    });

    this.socket.on('pos-agent:frame', (data: { controlId: string; jpeg: string }) => {
      if (data.controlId !== this.currentDesktopControlId) return;
      this.currentFrameSrc$.next('data:image/jpeg;base64,' + data.jpeg);
    });

    this.socket.on('pos-agent:control-ended', (data: { controlId: string }) => {
      if (data.controlId === this.currentDesktopControlId) {
        this.cleanupDesktop();
      }
    });

    this.socket.on('pos-agent:error', (data: { message: string }) => {
      console.error('[Desktop View] pos-agent error:', data.message);
    });

    // Register PosAgentAdminService listeners on the same socket (for DesktopOverlayComponent)
    this.posAgentAdminService.registerSocketListeners(this.socket);
  }

  loadOnlineAgents(): void {
    const baseUrl = environment.baseUrl;
    this.http.get<any>(`${baseUrl}pos/remote/online-agents`).subscribe({
      next: (res) => {
        const agents: PosAgent[] = res?.response?.data?.agents || [];
        this.onlineAgents$.next(agents);
      },
      error: (err) => {
        console.error('[Desktop View] Error loading online agents:', err);
      },
    });
  }

  requestDesktopControl(terminalId: string, agent: PosAgent): void {
    const controlId = 'desk-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8);
    this.currentDesktopControlId = controlId;
    this.activeDesktopSession$.next({
      controlId,
      terminalId,
      screenWidth: agent.screenWidth,
      screenHeight: agent.screenHeight,
    });
    this.socket?.emit('pos-agent:control-request', { controlId, terminalId });
    console.log('[Desktop View] control-request sent | controlId:', controlId, '| terminalId:', terminalId);
  }

  sendDesktopInput(
    controlId: string,
    event: MouseEvent | WheelEvent | KeyboardEvent,
    imgEl: HTMLImageElement,
    screenWidth: number,
    screenHeight: number,
  ): void {
    if (!this.socket?.connected) return;

    if (event instanceof WheelEvent) {
      const { normX, normY } = this.getNormalizedCoordsFromImg(event, imgEl, screenWidth, screenHeight);
      this.socket.emit('pos-agent:input', { controlId, type: 'wheel', normX, normY, deltaY: event.deltaY });
    } else if (event instanceof KeyboardEvent) {
      this.socket.emit('pos-agent:input', {
        controlId,
        type: event.type,
        normX: 0,
        normY: 0,
        key: event.key,
        code: event.code,
        modifiers: {
          shift: event.shiftKey,
          ctrl: event.ctrlKey,
          alt: event.altKey,
          meta: event.metaKey,
        },
      });
    } else {
      const { normX, normY } = this.getNormalizedCoordsFromImg(event, imgEl, screenWidth, screenHeight);
      const typeMap: Record<string, string> = {
        mousemove: 'mousemove',
        mousedown: 'mousedown',
        mouseup: 'mouseup',
        dblclick: 'dblclick',
      };
      const type = typeMap[event.type] ?? event.type;

      if (type === 'mousemove') {
        const now = Date.now();
        if (now - this.lastDesktopMoveEmitMs < 100) return; // ~10fps cap for desktop move
        this.lastDesktopMoveEmitMs = now;
      }

      this.socket.emit('pos-agent:input', { controlId, type, normX, normY, button: event.button });
    }
  }

  endDesktopSession(): void {
    if (this.currentDesktopControlId) {
      this.socket?.emit('pos-agent:control-end', { controlId: this.currentDesktopControlId });
    }
    this.cleanupDesktop();
  }

  loadOnlineTablets(): void {
    const baseUrl = environment.baseUrl;
    this.http.get<any>(`${baseUrl}pos/remote/online-tablets`).subscribe({
      next: (res) => {
        const tablets: PosTablet[] = res?.response?.data?.tablets || [];
        this.onlineTablets$.next(tablets);
      },
      error: (err) => {
        console.error('Error loading online tablets:', err);
      },
    });
  }

  requestControl(posSocketId: string): void {
    const controlId = 'ctrl-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8);
    this.currentControlId = controlId;
    this.socket?.emit('pos:control-request', { controlId, posSocketId });
  }

  sendInputEvent(controlId: string, event: MouseEvent | KeyboardEvent | WheelEvent, videoEl: HTMLVideoElement): void {
    if (!this.socket?.connected) return;

    if (event instanceof WheelEvent) {
      const { normX, normY } = this.getNormalizedCoords(event, videoEl);
      this.socket.emit('pos:input-event', {
        controlId,
        type: 'wheel',
        normX,
        normY,
        deltaX: event.deltaX,
        deltaY: event.deltaY,
      });
    } else if (event instanceof MouseEvent) {
      const { normX, normY } = this.getNormalizedCoords(event, videoEl);

      let type: string;
      switch (event.type) {
        case 'mousemove': type = 'pointermove'; break;
        case 'mousedown': type = 'pointerdown'; break;
        case 'mouseup':   type = 'pointerup';   break;
        case 'dblclick':  type = 'dblclick';    break;
        default:          type = event.type;    break;
      }

      if (type === 'pointermove') {
        // Skip move events while a button is pressed — prevents Flutter gesture arena
        // from interpreting clicks as drags
        if (event.type === 'mousemove' && (event as MouseEvent).buttons > 0) {
          return;
        }
        const now = Date.now();
        if (now - this.lastMoveEmitMs < 33) return; // ~30fps cap
        this.lastMoveEmitMs = now;
      }

      console.log('[RC Admin] emit pos:input-event', type, { controlId, normX: normX.toFixed(3), normY: normY.toFixed(3) });
      this.socket.emit('pos:input-event', {
        controlId,
        type,
        normX,
        normY,
        button: event.button,
      });
    } else if (event instanceof KeyboardEvent) {
      console.log('[RC Admin] emit pos:input-event keyboard', { type: event.type, key: event.key });
      this.socket.emit('pos:input-event', {
        controlId,
        type: event.type,
        normX: 0,
        normY: 0,
        key: event.key,
        code: event.code,
        shiftKey: event.shiftKey,
        ctrlKey: event.ctrlKey,
        altKey: event.altKey,
        metaKey: event.metaKey,
      });
    }
  }

  endSession(): void {
    if (this.currentControlId) {
      this.socket?.emit('pos:control-end', { controlId: this.currentControlId });
    }
    this.cleanup();
  }

  disconnect(): void {
    this.cleanup();
    this.cleanupDesktop();
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.onlineTablets$.next([]);
    this.onlineAgents$.next([]);
  }

  private createPeerConnection(): void {
    this.peerConnection = new RTCPeerConnection(this.configuration);

    this.peerConnection.onicecandidate = (event) => {
      if (event.candidate) {
        this.socket?.emit('pos:ice-candidate', {
          controlId: this.currentControlId,
          candidate: event.candidate,
        });
      }
    };

    this.peerConnection.ontrack = (event) => {
      const current = this.activeSession$.getValue();
      if (current) {
        this.activeSession$.next({ ...current, stream: event.streams[0] });
      }
    };
  }

  private async handleOffer(sdp: RTCSessionDescriptionInit): Promise<void> {
    if (!this.peerConnection) {
      this.createPeerConnection();
    }
    if (!this.peerConnection) return;

    try {
      await this.peerConnection.setRemoteDescription(new RTCSessionDescription(sdp));
      const answer = await this.peerConnection.createAnswer();
      await this.peerConnection.setLocalDescription(answer);
      this.socket?.emit('pos:answer', { controlId: this.currentControlId, sdp: answer });
    } catch (err) {
      console.error('Error handling POS offer:', err);
    }
  }

  private async handleIceCandidate(candidate: RTCIceCandidateInit): Promise<void> {
    if (this.peerConnection) {
      try {
        await this.peerConnection.addIceCandidate(new RTCIceCandidate(candidate));
      } catch (err) {
        console.error('Error adding ICE candidate:', err);
      }
    }
  }

  private getNormalizedCoords(event: MouseEvent | WheelEvent, videoEl: HTMLVideoElement): { normX: number; normY: number } {
    if (!videoEl.videoWidth || !videoEl.videoHeight) {
      const normX = videoEl.clientWidth ? (event as any).offsetX / videoEl.clientWidth : 0;
      const normY = videoEl.clientHeight ? (event as any).offsetY / videoEl.clientHeight : 0;
      return { normX, normY };
    }

    const videoAspect = videoEl.videoWidth / videoEl.videoHeight;
    const elementAspect = videoEl.clientWidth / videoEl.clientHeight;

    let contentWidth: number, contentHeight: number, contentOffsetX: number, contentOffsetY: number;

    if (videoAspect > elementAspect) {
      // Video is wider than element — letterbox: black bars top and bottom
      contentWidth = videoEl.clientWidth;
      contentHeight = videoEl.clientWidth / videoAspect;
      contentOffsetX = 0;
      contentOffsetY = (videoEl.clientHeight - contentHeight) / 2;
    } else {
      // Video is taller than element — pillarbox: black bars left and right
      contentHeight = videoEl.clientHeight;
      contentWidth = videoEl.clientHeight * videoAspect;
      contentOffsetX = (videoEl.clientWidth - contentWidth) / 2;
      contentOffsetY = 0;
    }

    const relX = (event as any).offsetX - contentOffsetX;
    const relY = (event as any).offsetY - contentOffsetY;

    const normX = Math.max(0, Math.min(1, relX / contentWidth));
    const normY = Math.max(0, Math.min(1, relY / contentHeight));

    return { normX, normY };
  }

  private cleanup(): void {
    if (this.peerConnection) {
      this.peerConnection.close();
      this.peerConnection = null;
    }
    this.currentControlId = null;
    this.activeSession$.next(null);
  }

  private cleanupDesktop(): void {
    this.currentDesktopControlId = null;
    this.activeDesktopSession$.next(null);
    this.currentFrameSrc$.next(null);
  }

  private getNormalizedCoordsFromImg(
    event: MouseEvent | WheelEvent,
    imgEl: HTMLImageElement,
    screenWidth: number,
    screenHeight: number,
  ): { normX: number; normY: number } {
    const naturalW = imgEl.naturalWidth || screenWidth;
    const naturalH = imgEl.naturalHeight || screenHeight;
    const screenAspect = naturalW / naturalH;
    const elementAspect = imgEl.clientWidth / imgEl.clientHeight;

    let contentWidth: number, contentHeight: number, contentOffsetX: number, contentOffsetY: number;

    if (screenAspect > elementAspect) {
      // Wider than element — letterbox: black bars top/bottom
      contentWidth = imgEl.clientWidth;
      contentHeight = imgEl.clientWidth / screenAspect;
      contentOffsetX = 0;
      contentOffsetY = (imgEl.clientHeight - contentHeight) / 2;
    } else {
      // Taller than element — pillarbox: black bars left/right
      contentHeight = imgEl.clientHeight;
      contentWidth = imgEl.clientHeight * screenAspect;
      contentOffsetX = (imgEl.clientWidth - contentWidth) / 2;
      contentOffsetY = 0;
    }

    const relX = (event as any).offsetX - contentOffsetX;
    const relY = (event as any).offsetY - contentOffsetY;

    const normX = Math.max(0, Math.min(1, relX / contentWidth));
    const normY = Math.max(0, Math.min(1, relY / contentHeight));

    return { normX, normY };
  }
}
